import React, { useState } from 'react';
import './AdvancedGeneratedForm.css';

// Child component for individual form elements
const FormElement = ({ element, formData, setFormData, errors, handleInputChange, handleReset, isGlobal = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const baseStyle = element.styling || {};
  const hoverStyle = element.hoverStyle || {};
  const focusStyle = element.focusStyle || {};

  const currentStyle = {
    ...baseStyle,
    ...(isHovered ? hoverStyle : {}),
    ...(isFocused ? focusStyle : {})
  };

  const eventHandlers = {
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    onChange: handleInputChange
  };

  const value = formData[element.id] || '';
  const error = errors[element.id];

  // Apply alignment styles
  const containerStyle = {
    textAlign: element.alignment || 'left'
  };

  const renderElement = () => {
    switch (element.type) {
      case 'text':
      case 'email':
      case 'number':
      case 'tel':
      case 'url':
      case 'password':
      case 'date':
      case 'color':
      case 'range':
        return (
          <div className="input-wrapper">
            <input
              type={element.type}
              name={element.id}
              placeholder={element.placeholder}
              required={element.required}
              style={currentStyle}
              className={`form-input ${error ? 'error' : ''}`}
              value={value}
              min={element.validation?.min}
              max={element.validation?.max}
              step={element.type === 'range' ? '1' : undefined}
              {...eventHandlers}
            />
            {error && <span className="error-message">{error}</span>}
          </div>
        );

      case 'textarea':
        return (
          <div className="input-wrapper">
            <textarea
              name={element.id}
              placeholder={element.placeholder}
              required={element.required}
              style={currentStyle}
              className={`form-textarea ${error ? 'error' : ''}`}
              value={value}
              rows={element.rows || 4}
              {...eventHandlers}
            />
            {error && <span className="error-message">{error}</span>}
          </div>
        );

      case 'select':
        return (
          <div className="input-wrapper">
            <select
              name={element.id}
              required={element.required}
              style={currentStyle}
              className={`form-select ${error ? 'error' : ''}`}
              value={value}
              {...eventHandlers}
            >
              <option value="">{element.placeholder || 'Select an option'}</option>
              {element.options && element.options.map((option, idx) => (
                <option key={idx} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {error && <span className="error-message">{error}</span>}
          </div>
        );

      case 'checkbox':
        return (
          <label className="checkbox-label" style={{...element.labelStyle, ...containerStyle}}>
            <input
              type="checkbox"
              name={element.id}
              required={element.required}
              style={{ ...currentStyle, width: 'auto' }}
              className={`form-checkbox ${error ? 'error' : ''}`}
              checked={!!value}
              {...eventHandlers}
            />
            <span className="checkmark"></span>
            <span className="checkbox-text">{element.label}</span>
            {error && <span className="error-message">{error}</span>}
          </label>
        );

      case 'radio':
        return (
          <div className="radio-group" style={containerStyle}>
            <div className="field-label-main">
              {element.label}
              {element.required && <span className="required">*</span>}
            </div>
            {element.options && element.options.map((option, idx) => (
              <label key={idx} className="radio-label" style={element.labelStyle}>
                <input
                  type="radio"
                  name={element.id}
                  value={option}
                  required={element.required}
                  style={{ ...currentStyle, width: 'auto' }}
                  className={`form-radio ${error ? 'error' : ''}`}
                  checked={value === option}
                  {...eventHandlers}
                />
                <span className="radiomark"></span>
                <span className="radio-text">{option}</span>
              </label>
            ))}
            {error && <span className="error-message">{error}</span>}
          </div>
        );

      case 'file':
        return (
          <div className="input-wrapper">
            <input
              type="file"
              name={element.id}
              required={element.required}
              style={currentStyle}
              className={`form-file ${error ? 'error' : ''}`}
              onChange={handleInputChange}
            />
            {value && <div className="file-preview">Selected file: {value.name}</div>}
            {error && <span className="error-message">{error}</span>}
          </div>
        );

      case 'image':
        return (
          <div className="image-container" style={containerStyle}>
            {element.imageUrl ? (
              <img 
                src={element.imageUrl} 
                alt={element.altText} 
                style={currentStyle} 
                className="form-image" 
              />
            ) : (
              <div className="image-placeholder" style={currentStyle}>
                {element.altText || 'Image'}
              </div>
            )}
          </div>
        );

      case 'video':
        return (
          <div className="video-container" style={containerStyle}>
            {element.videoUrl ? (
              <video 
                src={element.videoUrl} 
                controls 
                style={currentStyle} 
                className="form-video" 
              />
            ) : (
              <div className="video-placeholder" style={currentStyle}>
                Video Element
              </div>
            )}
          </div>
        );

      case 'map':
        return (
          <div className="map-container" style={containerStyle}>
            <div 
              className="map-placeholder" 
              style={currentStyle}
            >
              Map: {element.lat}, {element.lng} (Zoom: {element.zoom})
            </div>
          </div>
        );

      case 'table':
        return (
          <div className="table-container" style={containerStyle}>
            <table style={currentStyle}>
              <tbody>
                {element.content && element.content.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex} style={element.cellStyle}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'link':
        return (
          <div className="link-container" style={containerStyle}>
            <a 
              href={element.href} 
              style={currentStyle} 
              className="form-link"
              onClick={(e) => e.preventDefault()} // Prevent actual navigation in preview
            >
              {element.text}
            </a>
          </div>
        );

      case 'container':
        return (
          <div className="container-element" style={currentStyle}>
            {element.content || 'Container'}
          </div>
        );

      case 'header':
        const HeaderTag = element.headerLevel || 'h2';
        return (
          <HeaderTag style={currentStyle} className="form-header">
            {element.label}
          </HeaderTag>
        );

      case 'divider':
        return <hr style={currentStyle} className="form-divider" />;

      case 'button':
        return (
          <div style={containerStyle}>
            <button
              type="button"
              style={currentStyle}
              className="form-button"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={() => {
                if (element.buttonAction === 'reset') handleReset();
                // Add other button actions here
              }}
            >
              {element.buttonText || 'Button'}
            </button>
          </div>
        );

      case 'submit':
        return (
          <div style={containerStyle}>
            <button
              type="submit"
              style={currentStyle}
              className="form-submit"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {element.buttonText || 'Submit'}
            </button>
          </div>
        );

      default:
        return (
          <div className="unknown-element" style={containerStyle}>
            Unknown element type: {element.type}
          </div>
        );
    }
  };

  return (
    <div className={`form-element-wrapper ${isGlobal ? 'global-element' : ''}`}>
      {renderElement()}
    </div>
  );
};

const AdvancedGeneratedForm = ({ formConfig, onSubmit, onEdit }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === 'file') {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else if (type === 'radio') {
      setFormData((prev) => ({ ...prev, [name]: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate global elements
    if (formConfig.globalElements) {
      formConfig.globalElements.forEach((element) => {
        if (element.required && !formData[element.id]) {
          newErrors[element.id] = element.validation?.errorMessage || `${element.label} is required`;
        }
        validateElement(element, formData[element.id], newErrors);
      });
    }

    // Validate section elements
    formConfig.sections.forEach((section) => {
      section.elements.forEach((element) => {
        if (element.required && !formData[element.id]) {
          newErrors[element.id] = element.validation?.errorMessage || `${element.label} is required`;
        }
        validateElement(element, formData[element.id], newErrors);
      });
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateElement = (element, value, newErrors) => {
    if (!value && !element.required) return;

    if (element.required && !value) {
      newErrors[element.id] = element.validation?.errorMessage || `${element.label} is required`;
      return;
    }

    if (value) {
      switch (element.type) {
        case 'email':
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            newErrors[element.id] = element.validation?.errorMessage || 'Please enter a valid email address';
          }
          break;
        case 'number':
          const numValue = parseFloat(value);
          if (isNaN(numValue)) {
            newErrors[element.id] = element.validation?.errorMessage || 'Please enter a valid number';
          } else {
            if (element.validation?.min && numValue < parseFloat(element.validation.min)) {
              newErrors[element.id] = element.validation?.errorMessage || `Value must be at least ${element.validation.min}`;
            }
            if (element.validation?.max && numValue > parseFloat(element.validation.max)) {
              newErrors[element.id] = element.validation?.errorMessage || `Value must be at most ${element.validation.max}`;
            }
          }
          break;
        case 'url':
          const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
          if (!urlRegex.test(value)) {
            newErrors[element.id] = element.validation?.errorMessage || 'Please enter a valid URL';
          }
          break;
        case 'tel':
          const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
          if (!phoneRegex.test(value.replace(/[\s\-\(\)]/g, ''))) {
            newErrors[element.id] = element.validation?.errorMessage || 'Please enter a valid phone number';
          }
          break;
        default:
          if (element.validation?.pattern) {
            const patternRegex = new RegExp(element.validation.pattern);
            if (!patternRegex.test(value)) {
              newErrors[element.id] = element.validation?.errorMessage || 'Please enter a valid value';
            }
          }
          break;
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    if (validateForm()) {
      try {
        if (onSubmit) {
          await onSubmit(formData);
        } else {
          // Default form submission behavior
          alert('Form submitted successfully!');
          console.log('Form Data:', formData);
        }
      } catch (error) {
        console.error('Form submission error:', error);
        alert('There was an error submitting the form. Please try again.');
      }
    } else {
      console.log('Form validation failed');
      const firstError = document.querySelector('.has-error');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    
    setIsSubmitting(false);
  };

  const handleReset = () => {
    setFormData({});
    setErrors({});
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  if (!formConfig) {
    return (
      <div className="no-form-config">
        <div className="empty-state">
          <h3>No Form Configuration</h3>
          <p>Please create a form using the form builder first.</p>
          {onEdit && (
            <button className="edit-button" onClick={onEdit}>
              Open Form Builder
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`advanced-generated-form ${isFullScreen ? 'fullscreen' : ''}`} style={formConfig.globalStyles}>
      <div className="form-container">
        <div className="form-actions">
          {onEdit && (
            <button className="edit-button" onClick={onEdit}>
              ✏️ Edit Form
            </button>
          )}
          <button className="fullscreen-button" onClick={toggleFullScreen}>
            {isFullScreen ? '⤢' : '⤡'}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="final-form" noValidate>
          {/* Render Global Elements */}
          {formConfig.globalElements && formConfig.globalElements.length > 0 && (
            <div className="global-elements-container">
              {formConfig.globalElements.map((element) => (
                <div
                  key={element.id}
                  className={`form-field global-form-field ${element.type} ${
                    errors[element.id] ? 'has-error' : ''
                  }`}
                >
                  {/* Don't show label for layout elements */}
                  {element.type !== 'header' &&
                    element.type !== 'divider' &&
                    element.type !== 'checkbox' &&
                    element.type !== 'radio' &&
                    element.type !== 'image' &&
                    element.type !== 'video' &&
                    element.type !== 'map' &&
                    element.type !== 'table' &&
                    element.type !== 'link' &&
                    element.type !== 'container' && (
                      <label style={element.labelStyle} className="field-label">
                        {element.label}
                        {element.required && <span className="required">*</span>}
                        {element.type === 'global' && <span className="global-badge">🌍</span>}
                      </label>
                    )}

                  <FormElement
                    element={element}
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    handleInputChange={handleInputChange}
                    handleReset={handleReset}
                    isGlobal={true}
                  />

                  {element.description && (
                    <div className="field-description">{element.description}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Render Section Elements */}
          {formConfig.sections.map((section) => (
            <div key={section.id} className="form-section" style={section.styling}>
              {section.title && (
                <div className="section-header">
                  <h3 className="section-title" style={section.titleStyling || {}}>
                    {section.title}
                  </h3>
                  {section.description && (
                    <p className="section-description">{section.description}</p>
                  )}
                </div>
              )}

              <div className={`section-content columns-${section.columns || 1}`}>
                {section.elements.map((element) => (
                  <div
                    key={element.id}
                    className={`form-field ${element.type} ${
                      errors[element.id] ? 'has-error' : ''
                    }`}
                  >
                    {/* Don't show label for layout elements */}
                    {element.type !== 'header' &&
                      element.type !== 'divider' &&
                      element.type !== 'checkbox' &&
                      element.type !== 'radio' &&
                      element.type !== 'image' &&
                      element.type !== 'video' &&
                      element.type !== 'map' &&
                      element.type !== 'table' &&
                      element.type !== 'link' &&
                      element.type !== 'container' && (
                        <label style={element.labelStyle} className="field-label">
                          {element.label}
                          {element.required && <span className="required">*</span>}
                        </label>
                      )}

                    <FormElement
                      element={element}
                      formData={formData}
                      setFormData={setFormData}
                      errors={errors}
                      handleInputChange={handleInputChange}
                      handleReset={handleReset}
                    />

                    {element.description && (
                      <div className="field-description">{element.description}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </form>
      </div>
    </div>
  );
};

export default AdvancedGeneratedForm;